import firebase from "firebase/app";
import { z } from "zod";

import { List, Modal } from "semantic-ui-react";

import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { db } from "astrid-firebase";
import distributeArticles, { getAutoDeliveries } from "astrid-firestore/src/api/distribution/distributeArticles";
import useCollectionData from "astrid-firestore/src/hooks/useCollectionData";

import Button from "../../../ui/components/Buttons/Button";
import Label from "../../../ui/components/Label/Label";
import LoadingContext from "../../../ui/components/LoadingContext/LoadingContext";
import ErrorMessage from "../../../ui/components/Messages/ErrorMessage";

import Form from "../../../forms/components/Form/Form";
import useSimpleForm from "../../../forms/hooks/useSimpleForm";
import Table from "../../../tables/components/Table";

import ArticleSegment from "../ArticleSegment/ArticleSegment";

import ArticleDistributeModalHeader from "./components/ArticleDistributeModalHeader";
import useDistributeArticlesChannelsTableColumns from "./hooks/useDistributeArticlesChannelsTableColumns";
import getArticleDistributeState from "./utils/getArticleDistributeState";

export default function ArticleDistributeModal({
	publisher,
	articles,
	delivery = false,
	channels: channelsProp,
	onClose,
}) {
	const { t } = useTranslation();

	const [results, setResults] = useState([]);
	const [isSubmitted, setIsSubmitted] = useState(false);
	const [advancedMode, setAdvancedMode] = useState(delivery);

	const [channels = channelsProp, loading] = useCollectionData(
		!channelsProp && db.collection("distributionChannels").where("deleted", "==", false),
	);

	const defaultDelivery = useMemo(() => {
		if (loading) {
			return {};
		}
		return (
			delivery ||
			articles
				.map((article) => getAutoDeliveries({ article, publisher, channels }))
				.reduce((acc, deliveries) => {
					deliveries?.forEach(({ channel, delivery }) => {
						acc[channel.id] = {
							artifact: acc[channel.id]?.artifact || !!delivery.artifact,
							metadata: acc[channel.id]?.metadata || !!delivery.metadata,
						};
					});

					return acc;
				}, {})
		);
	}, [articles, channels, delivery, publisher, loading]);

	const filteredChannels = channels?.filter((channel) => defaultDelivery[channel.id]);

	const { form, formState, onSubmit, error } = useSimpleForm({
		schema: z.object({
			delivery: z
				.record(
					z.string(),
					z.object({
						artifact: z.boolean(),
						metadata: z.boolean(),
					}),
				)
				.refine((delivery) => Object.values(delivery).some(({ artifact, metadata }) => artifact || metadata)),
			onlyDeliveryNeeded: z.boolean(),
		}),
		values: {
			delivery: defaultDelivery,
			onlyDeliveryNeeded: articles.length > 1,
		},
		onSubmit: ({ delivery, onlyDeliveryNeeded }) => {
			return distributeArticles(firebase, { articles, delivery, onlyDeliveryNeeded });
		},
		onSuccess: (results) => {
			setIsSubmitted(true);
			setResults(results);
		},
	});

	const columns = useDistributeArticlesChannelsTableColumns({ form, articles, channels: filteredChannels });

	const articlesWithError = results.filter(({ results }) => results?.some(({ error }) => error));

	const state = getArticleDistributeState({ publisher, formState, isSubmitted, filteredChannels, articlesWithError });

	return (
		<Modal size="tiny" open padded onClose={onClose}>
			<Modal.Content>
				<LoadingContext loading={loading}>
					<ArticleDistributeModalHeader state={state} />

					{state === "initial" && !advancedMode && (
						<List style={{ textAlign: "center", marginBottom: "1em" }}>
							{channels
								?.filter((channel) => Object.values(defaultDelivery[channel.id] || {}).some(Boolean))
								.map((channel) => (
									<List.Item key={channel.id}>
										<Label>{channel.name}</Label>
									</List.Item>
								))}
						</List>
					)}

					{state === "initial" && advancedMode && filteredChannels?.length > 0 && (
						<Form form={form}>
							<Table basic style={{ padding: 20 }} data={filteredChannels} columns={columns} />

							{articles.length > 1 && (
								<Form.Checkbox
									name="onlyDeliveryNeeded"
									label={t("onlyDeliverIfNeeded", "Only deliver if necessary")}
								/>
							)}
						</Form>
					)}

					{articlesWithError.map(({ article, results }) => (
						<ArticleSegment key={article.id} article={article}>
							{results.map(({ channel, error }) => (
								<ErrorMessage key={channel.id} error={error} header={channel.name} />
							))}
						</ArticleSegment>
					))}

					{error && <ErrorMessage error={error} />}
				</LoadingContext>
			</Modal.Content>

			<Modal.Actions>
				{state === "initial" && !advancedMode && (
					<Button basic floated="left" style={{ margin: 0 }} onClick={() => setAdvancedMode(true)}>
						{t("advanced", "Advanced")}
					</Button>
				)}

				<Button content={t("close", "Close")} onClick={onClose} />

				{state === "initial" && (
					<Button primary disabled={!formState.isValid} onClick={onSubmit}>
						{t("deliver", "Deliver")}
					</Button>
				)}
			</Modal.Actions>
		</Modal>
	);
}
